import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  listForgeApps,
  removeForgeApp,
  setForgeApp,
} from '@/lib/api/generated/api';
import { expectStatus } from '@/lib/api/client';

import { appRefusal, appRequest } from '../forgeApps';
import { FORGE_APPS_COPY as COPY } from '../linkedAccountsCopy';
import { organizationKeys } from '../queryKeys';

/** The service refused the secret (422): a new app needs one. */
export class ForgeAppSecretRefused extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ForgeAppSecretRefused';
  }
}

/** No workspace connects to the host any more (404). */
export class ForgeAppHostGone extends Error {
  constructor(host: string) {
    super(COPY.hostGone(host));
    this.name = 'ForgeAppHostGone';
  }
}

export function isForgeAppFormError(
  error: unknown,
): error is ForgeAppSecretRefused | ForgeAppHostGone {
  return (
    error instanceof ForgeAppSecretRefused || error instanceof ForgeAppHostGone
  );
}

/** Each forge host's sign-in app and the redirect address (spec 161 US1). */
export function useForgeApps() {
  return useQuery({
    queryKey: organizationKeys.forgeApps(),
    queryFn: async ({ signal }) =>
      expectStatus(await listForgeApps({ signal }), 200, COPY.loadFailed),
    retry: false,
  });
}

/**
 * Set up or change a host's app. The secret is passed straight to the service
 * and kept nowhere else; an empty one keeps the stored secret. Refusals come
 * back as typed errors for the form to say; anything else is a toast.
 */
export function useSetForgeApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      host,
      clientId,
      secret,
    }: {
      host: string;
      clientId: string;
      secret: string;
      /** Whether the host had an app, for the toast's words. */
      existed: boolean;
    }) => {
      const response = await setForgeApp(host, appRequest(clientId, secret));
      if (response.status === 200) return response.data;
      if (response.status === 422) {
        throw new ForgeAppSecretRefused(
          appRefusal(response.data)?.message ?? COPY.saveFailed(host),
        );
      }
      if (response.status === 404) throw new ForgeAppHostGone(host);
      throw new Error(COPY.saveFailed(host));
    },
    onSuccess: async (_app, { host, existed }) => {
      await queryClient.invalidateQueries({
        queryKey: organizationKeys.forgeApps(),
      });
      toast.success(existed ? COPY.updated(host) : COPY.saved(host));
    },
    onError: (error, { host }) => {
      if (isForgeAppFormError(error)) return;
      toast.error(COPY.saveFailed(host));
    },
  });
}

/** Remove a host's app; links made with it break at their next renewal. */
export function useRemoveForgeApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ host }: { host: string }) => {
      const response = await removeForgeApp(host);
      if (response.status !== 204) throw new Error(COPY.removeFailed(host));
    },
    onSuccess: async (_result, { host }) => {
      await queryClient.invalidateQueries({
        queryKey: organizationKeys.forgeApps(),
      });
      toast.success(COPY.removed(host));
    },
    onError: (_error, { host }) => {
      toast.error(COPY.removeFailed(host));
    },
  });
}
